"use client";
import React from "react";
import { motion } from "framer-motion";
import { useState } from "react";
import { usePathname } from "next/navigation";
import { SparklesIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useSmoothScroll } from "@/utils/smooth-scroll";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();
  const { scrollToSection } = useSmoothScroll({
    duration: 800, // Animation plus rapide pour plus de réactivité
    offset: -80, // Ajustement pour la hauteur de la navbar
  });

  const navItems = [
    { label: "Accueil", href: "#hero", fullHref: "/#hero" },
    { label: "À propos", href: "#about", fullHref: "/#about" },
    { label: "Notre solution", href: "#solution", fullHref: "/#solution" },
    { label: "Comment ça marche", href: "#how-it-works", fullHref: "/#how-it-works" },
    { label: "FAQ", href: "#faq", fullHref: "/#faq" }
  ];

  // Fonction pour gérer le clic sur un lien de navigation
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, item: { href: string; fullHref: string }) => {
    e.preventDefault();
    
    // Si on est sur la page d'accueil, utiliser le smooth scroll
    if (pathname === "/") {
      scrollToSection(item.href);
    } else {
      // Sinon, rediriger vers la page d'accueil avec l'ancre
      window.location.href = item.fullHref;
    }
  };

  return (
    <motion.nav 
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="fixed top-0 left-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-violet-200/30"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="/" className="flex items-center space-x-3 group">
            <div className="w-8 h-8 bg-gradient-to-r from-violet-500 to-purple-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <SparklesIcon className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">ConnectRH</span>
          </a>

          {/* Menu desktop */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item, index) => (
              <a
                key={index}
                href={pathname === "/" ? item.href : item.fullHref}
                onClick={(e) => handleNavClick(e, item)}
                className="text-gray-600 hover:text-violet-600 font-medium transition-colors duration-300 relative group cursor-pointer"
              >
                {item.label}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-violet-600 group-hover:w-full transition-all duration-300"></span>
              </a>
            ))}
          </div>

          {/* Bouton Se connecter */}
          <div className="hidden md:flex items-center space-x-4">
            <a
              href="/login"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:scale-105"
            >
              <span>Se connecter</span>
            </a>
          </div>

          {/* Menu mobile button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-violet-50 transition-colors duration-300"
          >
            {isOpen ? (
              <XMarkIcon className="w-6 h-6 text-gray-600" />
            ) : (
              <Bars3Icon className="w-6 h-6 text-gray-600" />
            )}
          </button>
        </div>

        {/* Menu mobile */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white border-t border-violet-200/30"
          >
            <div className="px-4 py-6 space-y-4">
              {navItems.map((item, index) => (
                <a
                  key={index}
                  href={pathname === "/" ? item.href : item.fullHref}
                  onClick={(e) => {
                    handleNavClick(e, item);
                    setIsOpen(false);
                  }}
                  className="block text-gray-600 hover:text-violet-600 font-medium py-2 transition-colors duration-300 cursor-pointer"
                >
                  {item.label}
                </a>
              ))}
              <a
                href="/login"
                onClick={() => setIsOpen(false)}
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white px-6 py-2.5 rounded-xl font-semibold w-full justify-center mt-4"
              >
                <span>Se connecter</span>
              </a>
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
}
